import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { syncCartItemWithProduct, type CatalogProductSnapshot } from '@/utils/cart';

export interface CartItem {
  productId: string;
  productName: string;
  brand: string;
  barcode: string;
  salePrice: number;
  originalSalePrice?: number;
  isPriceEdited: boolean;
  priceEditReason?: string;
  discountPercentage?: number;
  quantity: number;
  stock: number;
  unit?: string;
  isManualProduct?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateItemPrice: (productId: string, newPrice: number, reason?: string) => void;
  updateItemDiscount: (productId: string, discountPercentage: number) => void;
  syncCatalogProduct: (product: CatalogProductSnapshot) => void;
  clearCart: () => void;
  replaceCart: (items: CartItem[]) => void;
  totalItems: number;
  subtotal: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const updateItemPrice = useCallback((productId: string, newPrice: number, reason?: string) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.productId !== productId) return i;
        const originalPrice = i.isPriceEdited ? i.originalSalePrice : i.salePrice;
        return {
          ...i,
          salePrice: newPrice,
          originalSalePrice: originalPrice ?? i.salePrice,
          isPriceEdited: newPrice !== originalPrice,
          priceEditReason: reason || undefined,
        };
      })
    );
  }, []);

  const addToCart = useCallback((item: CartItem) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === item.productId);
      if (existing) {
        return prev.map((i) =>
          i.productId === item.productId
            ? { ...i, quantity: Math.round((i.quantity + item.quantity) * 1000) / 1000, isPriceEdited: false, originalSalePrice: undefined, priceEditReason: undefined }
            : i
        );
      }
      return [...prev, { ...item, isPriceEdited: false }];
    });
  }, []);

  const updateItemDiscount = useCallback((productId: string, discountPercentage: number) => {
    const normalized = Math.min(100, Math.max(0, Number.isFinite(discountPercentage) ? discountPercentage : 0));
    setItems((previous) => previous.map((item) => item.productId === productId
      ? { ...item, discountPercentage: normalized }
      : item));
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    const normalizedQuantity = Math.round(quantity * 1000) / 1000;
    setItems((prev) =>
      prev.map((i) =>
        i.productId === productId ? { ...i, quantity: normalizedQuantity } : i
      )
    );
  }, []);

  const syncCatalogProduct = useCallback((product: CatalogProductSnapshot) => {
    setItems((previous) => previous.map((item) => syncCartItemWithProduct(item, product)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const replaceCart = useCallback((nextItems: CartItem[]) => {
    setItems(nextItems.map((item) => ({ ...item })));
  }, []);

  // The cart badge represents product lines, not kilograms or fractional units.
  const totalItems = items.length;
  const subtotal = items.reduce((sum, i) => sum + i.salePrice * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateItemPrice,
        updateItemDiscount,
        syncCatalogProduct,
        clearCart,
        replaceCart,
        totalItems,
        subtotal,
        isCartOpen,
        setIsCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider');
  }
  return ctx;
}
